import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MapPin, Phone, Navigation } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { EVENT, PHOTOS, VENUE_TABS } from "@/lib/data";

const Venue = () => (
  <section id="venue" className="relative py-16 sm:py-24">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHeading
        eyebrow="The Venue"
        title={
          <>
            The Saban Theatre, <em className="text-gold-gradient italic">Beverly Hills</em>
          </>
        }
        sub="A 1930 Art Deco landmark bathed in candlelight — here is everything you need for a flawless evening."
      />

      <Reveal className="mt-14">
        <div className="relative rounded-[32px] overflow-hidden gold-ring">
          <img
            src={PHOTOS.theatre}
            alt="Inside the historic Saban Theatre in Beverly Hills"
            className="w-full aspect-[16/9] lg:aspect-[21/9] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1021] via-[#0B1021]/30 to-transparent" />
          <div className="absolute bottom-0 inset-x-0 p-6 sm:p-10 flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow">{EVENT.venue}</p>
              <p className="font-display text-2xl sm:text-3xl text-[#FDFBF7] mt-2">{EVENT.address}</p>
            </div>
            <a
              href={EVENT.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="venue-directions-link"
              className="btn-gold rounded-full px-6 py-3 text-sm font-bold inline-flex items-center gap-2"
            >
              <Navigation size={15} /> Get Directions
            </a>
          </div>
        </div>
      </Reveal>

      <div className="mt-10 grid sm:grid-cols-3 gap-5">
        {[
          { icon: MapPin, t: "Historic Art Deco house", d: "Seats 1,838 guests under a gilded ceiling — arrive early for photos under the marquee." },
          { icon: Phone, t: "Theatre: " + EVENT.venuePhone, d: "Box office & general inquiries" },
          { icon: Phone, t: "Accessible seating: " + EVENT.accessibilityPhone, d: "Reserve wheelchair & companion spots in advance" },
        ].map((x, i) => (
          <Reveal key={x.t} delay={i * 0.08}>
            <div className="card-lift glass rounded-2xl p-6 h-full">
              <x.icon size={20} className="text-[#F3D068]" strokeWidth={1.5} />
              <p className="mt-3 text-[#FDFBF7] font-medium">{x.t}</p>
              <p className="text-xs text-[#9CA3AF] mt-1.5 leading-relaxed">{x.d}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1} className="mt-12">
        <Tabs defaultValue="parking" className="w-full">
          <TabsList className="glass rounded-full p-1.5 flex-wrap justify-center h-auto gap-1 w-full sm:w-auto sm:mx-auto grid grid-cols-2 sm:flex" data-testid="venue-parking-tab-trigger">
            {VENUE_TABS.map((t) => (
              <TabsTrigger
                key={t.id}
                value={t.id}
                className="rounded-full px-5 py-2.5 text-xs sm:text-sm data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0B1021] text-[#C5CEE0]"
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {VENUE_TABS.map((t) => (
            <TabsContent key={t.id} value={t.id} className="mt-8">
              <h3 className="font-display text-2xl sm:text-3xl text-[#FDFBF7]">{t.title}</h3>
              <div className="grid md:grid-cols-2 gap-5 mt-7">
                {t.items.map((item) => (
                  <div key={item.h} className="border border-white/10 bg-[#131B36]/60 rounded-2xl p-6">
                    <p className="text-[#F3D068] font-medium text-sm tracking-wide">{item.h}</p>
                    <p className="text-sm text-[#C5CEE0] font-light mt-2.5 leading-relaxed">{item.p}</p>
                  </div>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </Reveal>
    </div>
  </section>
);

export default Venue;
